import { useAuth } from "@clerk/clerk-react";
import { Box, Button, Typography } from "@mui/material";
import NumberFlow from "@number-flow/react";
import { AlertContext } from "contexts/Alert";
import { BentoCategoriesContext } from "contexts/BentoCategories";
import { OrganizationUserContext } from "contexts/Organization";
import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { SearchParams, Sort } from "schemas/dashboard";
import { routes } from "schemas/routes";

import { fetcherAuth } from "utils/api";
import { trackEvent } from "utils/tracking";

import { Icon } from "../Gamification/components/shared/icons";
import { Pill } from "../Gamification/components/shared/primitives";
import HomeCard from "../components/HomeCard";
import EditPitchFiltersDrawer from "./EditPitchFiltersDrawer";
import styles from "./styles";

const MAX_VISIBLE_NICHES = 5;
const FALLBACK_BRAND_COUNT = 10000;

const SendPitch = () => {
  const { getToken } = useAuth();
  const { setErrorAlert } = useContext(AlertContext);
  const { currentOrg } = useContext(OrganizationUserContext);
  const { topLevelCategories, fetchBentoCategories } = useContext(
    BentoCategoriesContext,
  );
  const navigate = useNavigate();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [matchCount, setMatchCount] = useState<number | null>(null);

  const selectedCategoryIds = currentOrg?.bentoCategories?.map(
    (category) => category.id,
  );

  useEffect(() => {
    if (currentOrg?.id && topLevelCategories.length === 0) {
      fetchBentoCategories();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id]);

  useEffect(() => {
    const fetchMatchCount = async () => {
      try {
        const { count } = await fetcherAuth(
          getToken,
          `/api/organization/${currentOrg?.id}/bento-brands/match-count`,
        );
        setMatchCount(count);
      } catch (error) {
        setErrorAlert(error);
      }
    };
    if (currentOrg?.id) {
      fetchMatchCount();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id, selectedCategoryIds?.join(",")]);

  const editFilters = () => {
    trackEvent("Edit Pitch Filters Button Clicked", {
      "Number of Niches": selectedCategoryIds?.length ?? 0,
    });
    setFiltersOpen(true);
  };

  const browseBrands = () => {
    trackEvent("Browse Brands Button Clicked", {
      "Number of Brands": matchCount,
      "Has Matches": matchCount !== null && matchCount > 0,
    });
    const params = new URLSearchParams();

    const selectedSet = new Set(selectedCategoryIds);
    const categoryParams: string[] = [];
    const consumed = new Set<number>();

    for (const category of topLevelCategories) {
      const chipIds = [
        category.id,
        ...(category.subcategories ?? []).map((sub) => sub.id),
      ];
      const selectedChipIds = chipIds.filter((id) => selectedSet.has(id));
      if (selectedChipIds.length === 0) continue;
      if (selectedChipIds.length === chipIds.length) {
        categoryParams.push(`${category.id}:all`);
      } else {
        categoryParams.push(...selectedChipIds.map((id) => `${id}`));
      }
      selectedChipIds.forEach((id) => consumed.add(id));
    }

    for (const id of selectedCategoryIds ?? []) {
      if (!consumed.has(id)) categoryParams.push(`${id}`);
    }

    if (categoryParams.length) {
      params.set(SearchParams.CATEGORY_ID_DISCOVER, categoryParams.join(","));
    }

    params.set(SearchParams.SORT_DISCOVER, Sort.RecentlyAdded);
    navigate(`/${routes.brands}/all?${params.toString()}`);
  };

  const selectedNiches = currentOrg?.bentoCategories ?? [];
  const visibleNiches = selectedNiches.slice(0, MAX_VISIBLE_NICHES);
  const extraNiches = selectedNiches.length - visibleNiches.length;

  const hasMatches = typeof matchCount === "number" && matchCount > 0;
  const displayCount = hasMatches ? matchCount : FALLBACK_BRAND_COUNT;

  return (
    <HomeCard
      sx={styles.card}
      icon={<Icon.Send size={20} />}
      iconSx={styles.iconBox}
      title="Send a pitch"
      subtitle={
        hasMatches
          ? "New brands added this week that match your filters."
          : "Explore all the brands added this week on our platform."
      }
      chip={
        <Pill sx={styles.badge}>
          <NumberFlow value={displayCount} prefix="+" /> this week
        </Pill>
      }
    >
      <Box sx={styles.bigStat}>
        <Typography component="span" sx={styles.bigStatNum}>
          <NumberFlow value={displayCount} suffix={hasMatches ? "" : "+"} />
        </Typography>
        <Typography component="span" sx={styles.bigStatUnit}>
          {hasMatches ? "brands in your niches" : "new brands added weekly"}
        </Typography>
      </Box>

      <Box sx={styles.filterRow}>
        <Typography component="span" sx={styles.filterLabel}>
          Niches
        </Typography>
        {selectedNiches.length === 0 ? (
          <Box
            component="span"
            sx={styles.nicheAdd}
            onClick={() => setFiltersOpen(true)}
          >
            <Icon.Plus size={13} />
            Add
          </Box>
        ) : (
          <Box sx={styles.chipList}>
            {visibleNiches.map((niche) => (
              <Box key={niche.id} component="span" sx={styles.niche}>
                {niche.displayName || niche.name}
              </Box>
            ))}
            {extraNiches > 0 && (
              <Box component="span" sx={styles.nicheMore}>
                +{extraNiches} more
              </Box>
            )}
          </Box>
        )}
      </Box>

      <Box sx={styles.actions}>
        <Button
          disableElevation
          sx={styles.ctaPrimary}
          endIcon={<Icon.ArrowRight size={14} />}
          onClick={browseBrands}
        >
          Browse brands
        </Button>
        <Button disableElevation sx={styles.ctaGhost} onClick={editFilters}>
          Edit filters
        </Button>
      </Box>

      <EditPitchFiltersDrawer
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
      />
    </HomeCard>
  );
};

export default SendPitch;
